// Feature: MCRO001-F0026; related MCRO001-F0021 attachment authority and MCRO001-F0006 library presentation.
// Local Web slice touches T-MCRO-026 cases A-K; canonical case L limits the evidence boundary.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createProportionalTimingMap,
  DemonstrationTimingMapError,
  moveTimingSyncPoint,
  projectMediaMsToCount,
  stepTimingSyncPoint,
  validateDemonstrationTimingMap,
} from './demonstrationTimingMap';
import { DRAG_MIME } from './MediaLibraryGrid';

const text = (value) => typeof value === 'string' ? value.trim() : '';
const whole = (value) => typeof value === 'number' && Number.isInteger(value) ? value : null;
const unavailable = () => ({ valid: false, reason: 'Demonstration setup is unavailable.' });

export function demonstrationRevisionBoundary(value) {
  const revisionId = text(value?.revisionId);
  const snapshotDigest = text(value?.snapshotDigest);
  if (value?.ready !== true || !revisionId || !snapshotDigest) return unavailable();
  return { valid: true, revisionId, snapshotDigest };
}

export function demonstrationMediaBoundary(item, eligibility) {
  const mediaId = text(item?.mediaId) || text(item?.id);
  const relationId = text(eligibility?.relationId);
  const expectedRelationVersion = whole(eligibility?.expectedRelationVersion);
  const renditionId = text(eligibility?.renditionId);
  const expectedRenditionVersion = whole(eligibility?.expectedRenditionVersion);
  const durationMs = whole(eligibility?.durationMs);
  const approvedPreviewUrl = text(eligibility?.approvedPreviewUrl);
  const frameStepMs = whole(eligibility?.frameStepMs);
  if (eligibility?.eligible !== true || !mediaId || !relationId || expectedRelationVersion === null
      || expectedRelationVersion < 0 || !renditionId || expectedRenditionVersion === null
      || expectedRenditionVersion < 0 || durationMs === null || durationMs <= 0
      || !approvedPreviewUrl || frameStepMs === null || frameStepMs <= 0) return unavailable();
  return {
    valid: true,
    mediaId,
    relationId,
    expectedRelationVersion,
    renditionId,
    expectedRenditionVersion,
    durationMs,
    approvedPreviewUrl,
    frameStepMs,
  };
}

export function completeDemonstrationBars(authoredBars = [], revisionId) {
  return authoredBars.filter((bar) => bar?.revisionId === revisionId
    && Number.isInteger(bar?.startCount)
    && Number.isInteger(bar?.endCount)
    && bar.endCount - bar.startCount === 7);
}

function markerBaseLabel(point) {
  if (point.kind === 'start') return `Video start · count ${point.count}`;
  if (point.kind === 'end') return `Video end · count ${point.count}`;
  return `${point.sectionLabel} starts · count ${point.count}`;
}

function formatTime(mediaMs) {
  return `${(mediaMs / 1000).toFixed(3)} seconds`;
}

function safeModelMessage(cause, fallback) {
  return cause instanceof DemonstrationTimingMapError ? cause.message : fallback;
}

// T-MCRO-026 case J — deliver a pending focus restoration, or keep it pending.
//
// Every control this workspace restores focus to is disabled while a save is in
// flight (Save is `disabled={frozen || !canUseAdapter}`, the library launcher is
// `disabled={saving}`). Calling focus() on a disabled control is a no-op by the
// HTML focusability rules: it neither throws nor reports failure. So a request
// that is consumed on the first commit after it was set is SILENTLY DROPPED
// whenever that commit still has `saving === true` — the author is left on
// <body> with no focus position at all.
//
// The request is therefore only consumed once focus actually landed. If the
// target is still unfocusable the request stays pending and the next commit
// retries it; `setSaving(false)` guarantees that commit arrives.
//
// Returns 'idle' | 'delivered' | 'retained' | 'dropped'.
export function deliverFocusRequest(focusRequestRef) {
  const target = focusRequestRef?.current;
  if (!target) return 'idle';
  // The target has left the DOM (the editor unmounted): it can never take focus
  // now, so drop the request rather than retrying it forever.
  if (!target.isConnected || typeof target.focus !== 'function') {
    focusRequestRef.current = null;
    return 'dropped';
  }
  target.focus();
  if ((target.ownerDocument || document).activeElement !== target) return 'retained';
  focusRequestRef.current = null;
  return 'delivered';
}

function authorityKey(revision, media) {
  if (!revision?.valid || !media?.valid) return '';
  return [
    revision.revisionId,
    revision.snapshotDigest,
    media.mediaId,
    media.relationId,
    media.expectedRelationVersion,
    media.renditionId,
    media.expectedRenditionVersion,
    media.durationMs,
    media.approvedPreviewUrl,
    media.frameStepMs,
  ].join('\u001f');
}

export function DemonstrationWorkspace({
  items = [],
  revisionContext = {},
  sections = [],
  authoredBars = [],
  capabilities = {},
  actions = {},
  autoOpen = false,
}) {
  const [workspaceMode, setWorkspaceMode] = useState('attached');
  const [libraryWidth, setLibraryWidth] = useState(38);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState('');
  const [previewMs, setPreviewMs] = useState(0);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const generationRef = useRef(0);
  const pendingSaveRef = useRef(null);
  const videoRef = useRef(null);
  const focusRequestRef = useRef(null);
  const saveButtonRef = useRef(null);
  const autoOpenedKeyRef = useRef('');
  const revision = demonstrationRevisionBoundary(revisionContext);
  const fullBars = useMemo(
    () => completeDemonstrationBars(authoredBars, revision.valid ? revision.revisionId : ''),
    [authoredBars, revision.valid, revision.revisionId],
  );
  const canUseAdapter = capabilities.canEditTiming === true
    && typeof actions.getDemonstrationEligibility === 'function'
    && typeof actions.onSaveTimingMap === 'function';
  const candidates = useMemo(() => items.map((item) => {
    if (!revision.valid || !canUseAdapter) return { item, media: unavailable(), launchable: false };
    try {
      const supplied = actions.getDemonstrationEligibility({
        mediaId: text(item?.mediaId) || text(item?.id),
        revisionId: revision.revisionId,
        snapshotDigest: revision.snapshotDigest,
      });
      const media = supplied && typeof supplied.then !== 'function'
        ? demonstrationMediaBoundary(item, supplied)
        : unavailable();
      return { item, media, launchable: media.valid };
    } catch {
      return { item, media: unavailable(), launchable: false };
    }
  }), [actions, canUseAdapter, items, revision.revisionId, revision.snapshotDigest, revision.valid]);
  const filteredCandidates = useMemo(() => {
    const needle = query.toLowerCase().trim();
    if (!needle) return candidates;
    return candidates.filter(({ item }) => [item?.title, item?.teachingPoint, ...(item?.tags || [])]
      .some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [candidates, query]);
  const currentCandidate = draft
    ? candidates.find(({ media }) => media.valid && media.mediaId === draft.media.mediaId)
    : null;
  const currentAuthorityKey = authorityKey(revision, currentCandidate?.media);
  const stale = Boolean(draft && (draft.openedAuthorityKey !== currentAuthorityKey || !canUseAdapter));
  const selectedMarker = draft?.timingMap?.syncPoints.find((point) => point.syncPointId === selectedMarkerId) || null;
  const frozen = saving || stale;

  // T-MCRO-026 case C, entry variant: when this workspace is reached from a
  // media item the user already chose ("Timing editor" on a library card),
  // land directly in timing setup for that item instead of the generic
  // browse/pick state. This seeds the SAME chooseVideo(...) path "Add as a
  // demonstration" and the canvas drop target already use — no parallel
  // attach implementation. Keyed on the seeded item ids so it fires once per
  // distinct entry, not on every re-render (e.g. after Cancel).
  const seedItems = autoOpen ? items : [];
  const seedKey = seedItems.map((item) => text(item?.mediaId) || text(item?.id)).join('');
  const seedCandidate = seedItems.length
    ? candidates.find(({ item }) => (text(item?.mediaId) || text(item?.id)) === (text(seedItems[0]?.mediaId) || text(seedItems[0]?.id)))
    : null;
  const autoOpenBlockedReason = (() => {
    if (!seedItems.length) return '';
    if (capabilities.canEditTiming !== true) {
      return "You don't have permission to edit demonstration timing for this routine.";
    }
    if (!revision.valid) {
      return 'This routine revision is not ready for demonstration timing yet.';
    }
    if (!canUseAdapter) {
      return 'Demonstration timing setup is unavailable right now.';
    }
    if (seedCandidate && !seedCandidate.launchable) {
      return `"${seedItems[0]?.title || 'This video'}" is not eligible for demonstration timing right now (an approved rendition or a complete authored eight-count bar may be missing).`;
    }
    return '';
  })();

  useEffect(() => () => {
    generationRef.current += 1;
    pendingSaveRef.current = null;
  }, []);
  useEffect(() => {
    generationRef.current += 1;
    pendingSaveRef.current = null;
    setSaving(false);
  }, [canUseAdapter, currentAuthorityKey]);
  // T-MCRO-026 case J — see deliverFocusRequest above. This effect deliberately
  // has no dependency array: a request that could not be delivered yet (because
  // its target is still disabled by an in-flight save) must be re-attempted on
  // every commit. `setSaving(false)` guarantees the commit that re-enables the
  // target arrives, so the retry always terminates.
  useEffect(() => { deliverFocusRequest(focusRequestRef); });

  const chooseVideo = (candidate, launcher) => {
    if (pendingSaveRef.current !== null || !candidate?.launchable || !revision.valid || !canUseAdapter) return;
    generationRef.current += 1;
    setNotice('');
    if (fullBars.length === 0) {
      setDraft(null);
      setNotice('This revision has no complete authored 8-count bars available for demonstrations. Partial Rehearsal bars remain unchanged.');
      return;
    }
    try {
      const first = fullBars[0];
      const timingMap = createProportionalTimingMap({
        revisionId: revision.revisionId,
        renditionId: candidate.media.renditionId,
        durationMs: candidate.media.durationMs,
        mediaStartMs: 0,
        mediaEndMs: candidate.media.durationMs,
        startCount: first.startCount,
        endCount: first.endCount,
        sections,
        authoredBars: fullBars,
      }, revision.revisionId);
      setDraft({
        item: candidate.item,
        media: candidate.media,
        timingMap,
        openedAuthorityKey: authorityKey(revision, candidate.media),
        launcher,
      });
      setSelectedMarkerId(timingMap.syncPoints[0].syncPointId);
      setPreviewMs(0);
      setWorkspaceMode('attached');
    } catch (cause) {
      setDraft(null);
      setNotice(safeModelMessage(cause, 'Demonstration setup is unavailable.'));
    }
  };

  // Auto-invoke the same "Add as a demonstration" entry point for the seeded
  // item so a user who arrives from a chosen video lands directly in timing
  // setup instead of the browse/pick state. Guarded by seedKey so it fires
  // once per distinct seed set (not on every render, and not again after a
  // Cancel or Save clears the draft for the same entry).
  useEffect(() => {
    if (!seedItems.length || !seedCandidate) return;
    if (autoOpenedKeyRef.current === seedKey) return;
    if (!seedCandidate.launchable) return;
    autoOpenedKeyRef.current = seedKey;
    chooseVideo(seedCandidate, null);
  }, [seedKey, seedCandidate]);

  // M004-F0392 / T-M004-0013: the choreography canvas (this workspace, the
  // first consumer per M004-D015 §3/§4) is a DROP TARGET for media dragged
  // out of the library (MediaLibraryGrid's DRAG_MIME payload,
  // {orgId, mediaIds}). A drop attaches the FIRST currently-eligible dropped
  // media id as a demonstration by invoking the exact same chooseVideo(...)
  // entry point "Add as a demonstration" already uses — there is no second,
  // parallel attach implementation; this only adds a second way to reach the
  // one attach path MCRO001-D006 defines. The library's own obligation ends
  // at delivering the ids (M001-D013 §16.4: the library issues no grant);
  // this consumer owns what the drop means, per M004-F0392's design.
  const [dropHintDepth, setDropHintDepth] = useState(0);
  const isMediaDrag = (event) => Boolean(event?.dataTransfer?.types) && Array.from(event.dataTransfer.types).includes(DRAG_MIME);

  const onWorkspaceDragOver = useCallback((event) => {
    if (!isMediaDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);
  const onWorkspaceDragEnter = useCallback((event) => {
    if (!isMediaDrag(event)) return;
    event.preventDefault();
    setDropHintDepth((depth) => depth + 1);
  }, []);
  const onWorkspaceDragLeave = useCallback((event) => {
    if (!isMediaDrag(event)) return;
    event.preventDefault();
    setDropHintDepth((depth) => Math.max(0, depth - 1));
  }, []);
  const onWorkspaceDrop = useCallback((event) => {
    if (!isMediaDrag(event)) return;
    event.preventDefault();
    setDropHintDepth(0);
    let dropped;
    try { dropped = JSON.parse(event.dataTransfer.getData(DRAG_MIME) || '{}'); } catch { dropped = null; }
    const droppedIds = Array.isArray(dropped?.mediaIds) ? dropped.mediaIds.map(String) : [];
    if (droppedIds.length === 0) { setNotice('That drop did not carry any recognised media.'); return; }
    const attachable = droppedIds
      .map((mediaId) => candidates.find(({ item, launchable }) => launchable && (String(item?.mediaId) === mediaId || String(item?.id) === mediaId)))
      .find(Boolean);
    if (!attachable) {
      setNotice(droppedIds.length > 1
        ? 'None of the dropped media can be added as a demonstration right now.'
        : 'That media cannot be added as a demonstration right now.');
      return;
    }
    chooseVideo(attachable, null);
  }, [candidates, chooseVideo]);

  const rebuildRange = (startCount, endCount) => {
    if (!draft || frozen) return;
    try {
      const timingMap = createProportionalTimingMap({
        revisionId: revision.revisionId,
        renditionId: draft.media.renditionId,
        durationMs: draft.media.durationMs,
        mediaStartMs: 0,
        mediaEndMs: draft.media.durationMs,
        startCount,
        endCount,
        sections,
        authoredBars: fullBars,
      }, revision.revisionId);
      setDraft((current) => ({ ...current, timingMap }));
      setSelectedMarkerId(timingMap.syncPoints[0].syncPointId);
      setPreviewMs(0);
      setNotice('');
    } catch (cause) { setNotice(safeModelMessage(cause, 'The selected bar range is unavailable.')); }
  };

  const moveMarker = (syncPointId, mediaMs) => {
    if (!draft || frozen) return;
    try {
      const timingMap = moveTimingSyncPoint(draft.timingMap, syncPointId, Number(mediaMs), revision.revisionId);
      const next = timingMap.syncPoints.find((point) => point.syncPointId === syncPointId);
      setDraft((current) => ({ ...current, timingMap }));
      setPreviewMs(next.mediaMs);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = next.mediaMs / 1000;
      }
      setNotice('');
    } catch (cause) { setNotice(safeModelMessage(cause, 'The marker could not be moved.')); }
  };

  const stepMarker = (syncPointId, direction) => {
    if (!draft || frozen || !syncPointId) return;
    try {
      const timingMap = stepTimingSyncPoint(
        draft.timingMap,
        syncPointId,
        direction,
        draft.media.frameStepMs,
        revision.revisionId,
      );
      const next = timingMap.syncPoints.find((point) => point.syncPointId === syncPointId);
      setDraft((current) => ({ ...current, timingMap }));
      setSelectedMarkerId(syncPointId);
      setPreviewMs(next.mediaMs);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = next.mediaMs / 1000;
      }
      setNotice('');
    } catch (cause) { setNotice(safeModelMessage(cause, 'The marker could not be moved.')); }
  };

  const cancel = () => {
    generationRef.current += 1;
    pendingSaveRef.current = null;
    focusRequestRef.current = draft?.launcher || null;
    setSaving(false);
    setDraft(null);
    setSelectedMarkerId('');
    setNotice('Demonstration setup cancelled. The library item was not changed.');
  };

  const save = async () => {
    if (!draft || frozen || !canUseAdapter || pendingSaveRef.current !== null) return;
    let timingMap;
    try {
      timingMap = validateDemonstrationTimingMap(draft.timingMap, revision.revisionId);
    } catch (cause) {
      setNotice(safeModelMessage(cause, 'The timing map is invalid.'));
      focusRequestRef.current = saveButtonRef.current;
      return;
    }
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    pendingSaveRef.current = generation;
    setSaving(true);
    setNotice('');
    const immutableRequest = {
      mediaId: draft.media.mediaId,
      revision: { revisionId: revision.revisionId, snapshotDigest: revision.snapshotDigest },
      relation: { relationId: draft.media.relationId, expectedVersion: draft.media.expectedRelationVersion },
      rendition: { renditionId: draft.media.renditionId, expectedVersion: draft.media.expectedRenditionVersion },
      timingMap,
    };
    try {
      const result = await actions.onSaveTimingMap(immutableRequest);
      // T-MCRO-026 Case H — "commits none and removes/retains provisional state
      // honestly". A FAILED save is now always reported, even when this editor
      // session was superseded while the request was in flight.
      //
      // Previously the superseded check ran first and returned before saying
      // anything at all, so a failed save could leave the author with NO message
      // whatsoever — silence that reads as "nothing happened". Superseding is easy
      // to hit rather than exceptional: the authority-change effect above bumps
      // generationRef on any currentAuthorityKey change, and the container's
      // onSaveTimingMap refetches the demonstration list on BOTH its success and
      // failure paths, so a re-render mid-save is normal.
      const superseded = generation !== generationRef.current
        || draft.openedAuthorityKey !== currentAuthorityKey;
      if (!result || result.committed !== true) {
        setNotice('The demonstration could not be saved. No timing changes were committed.');
        if (!superseded) focusRequestRef.current = saveButtonRef.current;
        return;
      }
      // Committed, but against the authority context this editor was OPENED in.
      // If that context has since changed, the provisional row is deliberately
      // retained and no success is claimed here (Case H/K): the stale alert
      // already tells the author to cancel and reopen against the current
      // approved context.
      if (superseded) return;
      focusRequestRef.current = draft.launcher || null;
      setDraft(null);
      setSelectedMarkerId('');
      setNotice('Demonstration timing saved. The source video remains in the library.');
    } catch {
      if (generation === generationRef.current) {
        setNotice('The demonstration could not be saved. No timing changes were committed.');
        focusRequestRef.current = saveButtonRef.current;
      }
    } finally {
      if (generation === generationRef.current) {
        pendingSaveRef.current = null;
        setSaving(false);
      }
    }
  };

  const changePreview = (value) => {
    if (!draft || frozen) return;
    const mediaMs = Number(value);
    setPreviewMs(mediaMs);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = mediaMs / 1000;
    }
  };

  let previewCount = null;
  if (draft && previewMs >= draft.timingMap.mediaStartMs && previewMs <= draft.timingMap.mediaEndMs) {
    try { previewCount = projectMediaMsToCount(draft.timingMap, previewMs, draft.timingMap.revisionId); } catch { previewCount = null; }
  }

  return <section
    className={`mc-demonstration-workspace mode-${workspaceMode}`}
    style={{ '--mc-demo-library-width': `${libraryWidth}%` }}
    aria-label="Video demonstration workspace"
    onDragOver={onWorkspaceDragOver}
    onDragEnter={onWorkspaceDragEnter}
    onDragLeave={onWorkspaceDragLeave}
    onDrop={onWorkspaceDrop}
  >
    {dropHintDepth > 0 && (
      <div className="mc-demonstration-dropzone-overlay" role="status" aria-live="polite">
        <div className="mc-demonstration-dropzone-message"><span aria-hidden="true">⇩</span> Drop to add as a demonstration</div>
      </div>
    )}
    <header className="mc-demonstration-toolbar">
      <div><span className="mc-kicker">Video library</span><h2>Demonstrations</h2></div>
      <div className="mc-demonstration-view-actions" aria-label="Workspace view">
        <button type="button" disabled={saving} aria-pressed={workspaceMode === 'attached'} onClick={() => setWorkspaceMode('attached')}>Attached workspace</button>
        <button type="button" disabled={saving} aria-pressed={workspaceMode === 'library'} onClick={() => setWorkspaceMode('library')}>Library workspace</button>
      </div>
      <label className="mc-field mc-demonstration-resize"><span>Library panel width · {libraryWidth}%</span><input disabled={saving} aria-label="Library panel width" type="range" min="28" max="60" value={libraryWidth} onChange={(event) => setLibraryWidth(Number(event.target.value))} /></label>
    </header>
    <div className="mc-demonstration-grid">
      {workspaceMode === 'attached' && <section className="mc-panel mc-demonstration-editor" aria-label="Attached demonstration setup">
        {!draft ? (
          autoOpenBlockedReason
            ? <div className="mc-empty mc-demonstration-auto-open-blocked" role="status"><strong>Timing editor unavailable.</strong><span>{autoOpenBlockedReason}</span></div>
            : <div className="mc-empty"><strong>No provisional demonstration.</strong><span>Choose “Add as a demonstration” from an eligible library video. Setup stays on this page and does not alter the video.</span></div>
        ) : <>
          {seedItems.length > 1 && (
            <nav className="mc-demonstration-seed-switch" aria-label="Selected videos">
              {seedItems.map((seedItem) => {
                const seedMediaId = text(seedItem?.mediaId) || text(seedItem?.id);
                const itemCandidate = candidates.find(({ item }) => (text(item?.mediaId) || text(item?.id)) === seedMediaId);
                const active = (text(draft.item?.mediaId) || text(draft.item?.id)) === seedMediaId;
                return <button
                  key={seedMediaId}
                  type="button"
                  aria-pressed={active}
                  disabled={saving || active || !itemCandidate?.launchable}
                  onClick={(event) => itemCandidate && chooseVideo(itemCandidate, event.currentTarget)}
                >{seedItem.title || 'Untitled video'}</button>;
              })}
            </nav>
          )}
          <header><div><span className="mc-kicker">Provisional row</span><h3>{draft.item.title || 'Untitled video'}</h3></div><span className="mc-provisional-pill">Setting up</span></header>
          {stale && <div className="mc-alert" role="alert">The revision, relation, rendition or editing authority changed while this setup was open. Cancel and reopen it against the current approved context.</div>}
          <video ref={videoRef} src={draft.media.approvedPreviewUrl} preload="metadata" controls onPlay={(event) => event.currentTarget.pause()} aria-label={`Paused setup video for ${draft.item.title || 'demonstration'}`} />
          <p className="mc-muted">Setup is paused. Moving a marker or preview control seeks the approved rendition without starting playback.</p>
          <div className="mc-inline">
            <label className="mc-field"><span>Start 8-beat bar</span><select disabled={frozen} aria-label="Start 8-beat bar" value={draft.timingMap.startCount} onChange={(event) => rebuildRange(Number(event.target.value), draft.timingMap.endCount)}>{fullBars.filter((bar) => bar.startCount <= draft.timingMap.endCount).map((bar) => <option key={`start-${bar.barId}`} value={bar.startCount}>{bar.label || `Counts ${bar.startCount}–${bar.endCount}`}</option>)}</select></label>
            <label className="mc-field"><span>End 8-beat bar</span><select disabled={frozen} aria-label="End 8-beat bar" value={draft.timingMap.endCount} onChange={(event) => rebuildRange(draft.timingMap.startCount, Number(event.target.value))}>{fullBars.filter((bar) => bar.endCount >= draft.timingMap.startCount).map((bar) => <option key={`end-${bar.barId}`} value={bar.endCount}>{bar.label || `Counts ${bar.startCount}–${bar.endCount}`}</option>)}</select></label>
          </div>
          <fieldset disabled={frozen} className="mc-demonstration-markers"><legend>Video and section markers</legend>{draft.timingMap.syncPoints.map((point) => {
            const baseLabel = markerBaseLabel(point);
            const accessibleLabel = `${baseLabel} at ${formatTime(point.mediaMs)}`;
            const outputId = `mc-demonstration-marker-${point.syncPointId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
            return <label key={point.syncPointId} className={selectedMarkerId === point.syncPointId ? 'selected' : ''}><button type="button" aria-label={accessibleLabel} aria-describedby={outputId} aria-pressed={selectedMarkerId === point.syncPointId} onClick={() => setSelectedMarkerId(point.syncPointId)} onKeyDown={(event) => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); stepMarker(point.syncPointId, event.key === 'ArrowLeft' ? -1 : 1); } }}>{baseLabel}</button><input aria-label={`${accessibleLabel} time control`} aria-describedby={outputId} type="range" min="0" max={draft.media.durationMs} step="1" value={point.mediaMs} onChange={(event) => moveMarker(point.syncPointId, event.target.value)} /><output id={outputId}>{formatTime(point.mediaMs)}</output></label>;
          })}</fieldset>
          <div className="mc-demonstration-frame-controls"><button type="button" onClick={() => stepMarker(selectedMarker?.syncPointId, -1)} disabled={!selectedMarker || frozen} aria-label={`Previous video frame · ${draft.media.frameStepMs} milliseconds`}>← Frame</button><button type="button" onClick={() => stepMarker(selectedMarker?.syncPointId, 1)} disabled={!selectedMarker || frozen} aria-label={`Next video frame · ${draft.media.frameStepMs} milliseconds`}>Frame →</button><span className="mc-muted">Approved frame step · {draft.media.frameStepMs} ms</span></div>
          <label className="mc-field"><span>Piecewise preview · {previewCount === null ? 'outside segment' : `count ${previewCount.toFixed(2)}`}</span><input disabled={frozen} aria-label="Piecewise demonstration preview" type="range" min={draft.timingMap.mediaStartMs} max={draft.timingMap.mediaEndMs} step="1" value={Math.min(draft.timingMap.mediaEndMs, Math.max(draft.timingMap.mediaStartMs, previewMs))} onChange={(event) => changePreview(event.target.value)} /></label>
          <div className="mc-demonstration-endpoint-controls"><button type="button" disabled={frozen} onClick={() => moveMarker(draft.timingMap.syncPoints[0].syncPointId, previewMs)}>Set video start</button><button type="button" disabled={frozen} onClick={() => moveMarker(draft.timingMap.syncPoints.at(-1).syncPointId, previewMs)}>Set video end</button></div>
          <footer><button type="button" className="mc-quiet" onClick={cancel} disabled={saving}>Cancel</button><button ref={saveButtonRef} type="button" className="mc-primary" onClick={save} disabled={frozen || !canUseAdapter}>{saving ? 'Saving…' : 'Save demonstration'}</button></footer>
        </>}
      </section>}
      <section className="mc-panel mc-demonstration-library" aria-label="Demonstration video library">
        <div className="mc-panel-head"><label className="mc-field"><span>Your Video Library</span><input disabled={saving} aria-label="Search practice videos" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search cue, tag or teaching point" /></label>{capabilities.canUpload === true && typeof actions.onUpload === 'function' ? <button type="button" disabled={saving} onClick={() => actions.onUpload()}>Upload video</button> : <button type="button" disabled>Upload unavailable</button>}</div>
        {filteredCandidates.length === 0 ? <p className="mc-empty">No matching examples yet.</p> : <div className="mc-media-grid">{filteredCandidates.map((candidate) => <article key={candidate.item.mediaId || candidate.item.id}><div className="mc-video-placeholder">▶</div><span>{candidate.item.kind === 'walkthrough_video' ? 'Walkthrough' : 'Library video'}</span><h3>{candidate.item.title || 'Untitled video'}</h3><p>{candidate.item.teachingPoint || candidate.item.description || 'No teaching note'}</p><small>{(candidate.item.tags || []).join(' · ') || 'No tags'}</small><div className="mc-demonstration-card-actions">{candidate.launchable ? <button type="button" disabled={saving} onClick={(event) => chooseVideo(candidate, event.currentTarget)}>Add as a demonstration</button> : <span className="mc-muted">Demonstration setup unavailable</span>}{capabilities.canShare === true && typeof actions.onShare === 'function' ? <button type="button" disabled={saving} onClick={() => actions.onShare(candidate.item)}>Share</button> : <button type="button" disabled>Share unavailable</button>}</div></article>)}</div>}
      </section>
    </div>
    {notice && <div className="mc-notice" role="status">{notice}</div>}
  </section>;
}

export default DemonstrationWorkspace;
